import { decorate, observable, action, computed } from 'mobx';

import StorageService from '../../services/storage';
import { formatDate } from '../../utils/date';
import { encodeUuids } from '../../utils/parse-encode-uuids';

import {
  getAllSchedules,
  getScheduleDetailsByUuid,
  deleteScheduleAssembly,
} from '../../apis/six-api/v1/schedules';
import { getScheduleDetailsByUuidV2 } from '../../apis/six-api/v2/schedules';

import { routerStore } from '../RouterStore';
import { modalStore } from '../ModalStore';

export default class SchedulingStore {
  isLoading = false;
  isFailure = false;

  data = {
    customer: {},
    schedules: [],
  };

  getAllSchedules = async () => {
    try {
      this.isLoading = true;

      const storagedUuid = StorageService.getUuid();

      const {
        data: { records },
      } = await getAllSchedules(storagedUuid);

      this.data.customer = records[0].customer;
      this.data.schedules = this.buildSchedulesWithHistory(
        records[0].schedules
      );
    } catch (error) {
      this.isLoading = false;
      this.isFailure = true;
    } finally {
      this.isLoading = false;
    }
  };

  getScheduleDetails = async uuid => {
    try {
      this.isLoading = true;

      const {
        data: {
          records: [{ customer, schedules }],
        },
      } = await getScheduleDetailsByUuid(uuid);

      this.data.customer = customer;
      this.data.schedules = schedules;
    } catch (error) {
      this.isLoading = false;
      this.isFailure = true;
    } finally {
      this.isLoading = false;
    }
  };

  getScheduleDetailsV2 = async uuids => {
    try {
      this.isLoading = true;

      const response = await getScheduleDetailsByUuidV2(encodeUuids(uuids));

      this.data.customer = response.data.records[0].customer;

      const schedules = response.data.records.map(record => ({
        ...record.schedule,
      }));

      this.data.schedules = schedules;
    } catch (error) {
      this.isLoading = false;
      this.isFailure = true;
    } finally {
      this.isLoading = false;
    }
  };

  cancelScheduleAssembly = async (uuid, integrationId, customerName) => {
    const payload = {
      integration: {
        id: integrationId,
      },
      date: formatDate(new Date(), 'YYYY-MM-DD'),
    };

    let statusCode;

    try {
      this.isFailure = false;
      this.isLoading = true;

      const response = await deleteScheduleAssembly(uuid, payload);

      statusCode = response.status;
    } catch (error) {
      this.isFailure = true;
      this.isLoading = false;
    } finally {
      this.isLoading = false;
    }

    if (statusCode) {
      modalStore.closeModal();
      routerStore.push('/cancelado-com-sucesso', {
        customer: { name: customerName },
      });
    }
  };

  get productsAvailableToScheduling() {
    return this.data.schedules.filter(schedule => !schedule.scheduledDate);
  }

  get productsAlreadyScheduled() {
    return this.data.schedules.filter(schedule => schedule.scheduledDate);
  }

  get productsAlreadyScheduledAndGroupedByDate() {
    return Object.entries(
      this.productsAlreadyScheduled
        .map(item => ({
          ...item,
          scheduledDate: formatDate(item.scheduledDate, 'YYYY-MM-DD'),
        }))
        .reduce((acc, obj) => {
          const key = obj.scheduledDate;

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push(obj);

          return acc;
        }, {})
    ).map(item => ({ [item[0]]: item[1] }));
  }

  getSchedulingHistoryReasonAndDescription = id => {
    switch (id) {
      case 5:
        return {
          reason: 'Cliente ausente',
          description:
            'O nosso montador esteve na sua casa, mas infelizmente não te encontrou. Mas não se preocupe, você pode reagendar a visita do montador.',
        };
      case 6:
        return {
          reason: 'Montagem não realizada',
          description:
            'Sua montagem não foi feita no dia agendado, mas não se preocupe, você pode reagendar a visita do montador.',
        };
      default:
        return {
          reason: '',
          description: '',
        };
    }
  };

  buildSchedulesWithHistory = schedules => {
    return schedules.map(schedule => {
      if (schedule.history) {
        return {
          ...schedule,
          history: {
            ...schedule.history,
            ...this.getSchedulingHistoryReasonAndDescription(
              schedule.history.id
            ),
          },
        };
      }

      return schedule;
    });
  };
}

decorate(SchedulingStore, {
  isLoading: observable,
  isFailure: observable,
  showMessage: observable,
  data: observable,
  getAllSchedules: action,
  getScheduleDetails: action,
  cancelScheduleAssembly: action,
  productsAvailableToScheduling: computed,
  productsAlreadyScheduled: computed,
  productsAlreadyScheduledAndGroupedByDate: computed,
});

const schedulingStore = new SchedulingStore();

export { schedulingStore };
